import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const toAbsoluteUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/admin/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return toAbsoluteUrl(r.data.url);
};

/**
 * Media editing block for a listing: cover photo, gallery thumbnails,
 * upload, remove, and "re-host from URL" actions. Extracted from
 * AdminListingSheet to keep that component under the 300-line guideline.
 */
export const ListingMediaSection = ({
  data,
  setField,
  setImages,
  imagesAsArray,
}) => {
  const { t } = useTranslation();
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [rehostUrl, setRehostUrl] = useState("");
  const [rehosting, setRehosting] = useState(false);

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadFile(file);
      setField("cover_image", url);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const onGalleryPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const f of files) {
        try { urls.push(await uploadFile(f)); }
        catch (err) { toast.error(`Upload failed: ${f.name}`); }
      }
      const current = imagesAsArray();
      setImages([...current, ...urls]);
      if (urls.length) toast.success(`${urls.length} photo(s) uploaded`);
    } finally {
      setUploadingGallery(false);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const removeGalleryImage = (idx) => {
    const current = imagesAsArray();
    setImages(current.filter((_, i) => i !== idx));
  };

  const rehostToGallery = async () => {
    const url = rehostUrl.trim();
    if (!url) { toast.error("Paste an image URL"); return; }
    setRehosting(true);
    try {
      const r = await api.post("/admin/uploads/from-url", { url });
      const absolute = toAbsoluteUrl(r.data.url);
      const current = imagesAsArray();
      setImages([...current, absolute]);
      setRehostUrl("");
      toast.success("Photo re-hosted to your storage");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-host failed");
    } finally {
      setRehosting(false);
    }
  };

  const rehostToCover = async () => {
    const url = rehostUrl.trim();
    if (!url) { toast.error("Paste an image URL"); return; }
    setRehosting(true);
    try {
      const r = await api.post("/admin/uploads/from-url", { url });
      setField("cover_image", toAbsoluteUrl(r.data.url));
      setRehostUrl("");
      toast.success("Cover photo re-hosted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-host failed");
    } finally {
      setRehosting(false);
    }
  };

  const imagesValue = Array.isArray(data.images) ? data.images.join(", ") : data.images;

  return (
    <section>
      <h3 className="overline mb-3">Media</h3>
      <div className="flex items-start gap-4">
        <div className="w-32 h-32 rounded-xl border border-[#EAE3D9] bg-[#F4EFE6] overflow-hidden flex items-center justify-center shrink-0">
          {data.cover_image ? (
            <img src={data.cover_image} alt="cover" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-8 w-8 text-[#8C7A6B]" />
          )}
        </div>
        <div className="flex-1">
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">Cover image URL</span>
            <input
              data-testid="cover-image"
              type="text"
              value={data.cover_image ?? ""}
              onChange={(e) => setField("cover_image", e.target.value)}
              className="input-line"
            />
          </label>
          <input ref={coverInputRef} type="file" accept="image/*" onChange={onCoverPick} className="hidden" data-testid="cover-file-input" />
          <button type="button" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}
                  data-testid="upload-cover-btn"
                  className="btn-secondary text-xs mt-3 inline-flex items-center gap-2">
            <Upload className="h-3.5 w-3.5" /> {uploadingCover ? "Uploading…" : "Upload cover photo"}
          </button>
        </div>
      </div>

      <label className="block mt-6">
        <span className="text-xs uppercase tracking-wider text-[#8C7A6B]">{t("admin.images_csv")}</span>
        <textarea
          data-testid="images-csv"
          value={imagesValue}
          onChange={(e) => setField("images", e.target.value)}
          rows={2}
          className="input-line resize-none"
        />
      </label>

      {Array.isArray(data.images) && data.images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4" data-testid="gallery-grid">
          {data.images.map((src, i) => (
            <div key={src || `img-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-[#EAE3D9] group">
              <img src={src} alt={`gallery ${i}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGalleryImage(i)}
                      data-testid={`remove-gallery-${i}`}
                      className="absolute top-1 end-1 h-6 w-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={onGalleryPick} className="hidden" data-testid="gallery-file-input" />
      <button type="button" onClick={() => galleryInputRef.current?.click()} disabled={uploadingGallery}
              data-testid="upload-gallery-btn"
              className="btn-secondary text-xs mt-3 inline-flex items-center gap-2">
        <Upload className="h-3.5 w-3.5" /> {uploadingGallery ? "Uploading…" : "Add gallery photos"}
      </button>

      {/* Re-host from URL */}
      <div className="mt-5 pt-5 border-t border-[#EAE3D9]" data-testid="rehost-block">
        <p className="overline mb-2">Re-host a photo from a URL</p>
        <p className="text-xs text-[#8C7A6B] mb-3 leading-relaxed">
          Paste any image URL (e.g. one of your own photos from Airbnb&apos;s CDN). We&apos;ll download it once and save it to your storage — your listings then never depend on the source URL.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            data-testid="rehost-url-input"
            value={rehostUrl}
            onChange={(e) => setRehostUrl(e.target.value)}
            placeholder="https://a0.muscache.com/im/pictures/…"
            className="input-line flex-1"
          />
          <button type="button" onClick={rehostToGallery} disabled={rehosting}
                  data-testid="rehost-gallery-btn"
                  className="btn-secondary text-xs whitespace-nowrap disabled:opacity-50">
            {rehosting ? "Re-hosting…" : "→ Add to gallery"}
          </button>
          <button type="button" onClick={rehostToCover} disabled={rehosting}
                  data-testid="rehost-cover-btn"
                  className="btn-ghost text-xs whitespace-nowrap disabled:opacity-50">
            → Use as cover
          </button>
        </div>
      </div>
    </section>
  );
};
